import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { parse } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { type IModalLoadingRef } from '@/components/ModalLoading';
import { useGlobalContext } from '@/context/global.context';
import { getTramiteDetail, type ITramiteGetPayload } from '@/services/tramite.service';

import { FORMAT_DATE } from '@/constants/date.constants';
import { notificationsConfig } from '@/helpers/notifications';
import { type FormTramiteValues } from '@/interfaces/model/don.interface';

import useApiData from './useApiData';
import useRouteTramite from './useRouteTramite';

export const useLoadTramite = (id?: number, autoLoad?: boolean) => {
	const navigate = useNavigate();
	const ROUTES_TRAMITE = useRouteTramite();
	const { user } = useGlobalContext();

	const [isLoading, setIsLoading] = useState(false);
	const refLoading = useRef<IModalLoadingRef>(null);

	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const { triggerFetch } = useApiData({ loading: ['getTramite'] as const });
	const { reset, watch } = useFormContext<FormTramiteValues>();
	const hasData = Boolean(watch('id'));

	const tiposFirmaStr = { true: 'Telemática', false: 'Presencial' } as const;

	const loadData = useCallback(async (payload: ITramiteGetPayload) => {
		setIsLoading(true);

		try {
			const { data } = await triggerFetch({
				fetch: getTramiteDetail,
				payload,
				name: 'getTramite',
			});

			if (data) {
				const { documentos = [], fechaVencimiento, fechaCreacion, ...rest } = data;

				// --- Mapeo al Formulario ---
				const formData = {
					...rest,
					documentos,
					// Si firmaTelematica es undefined es que no se ha informado, por lo que mostraremos vacio.
					tipoFirmaDesc: tiposFirmaStr[String(data?.firmaTelematica) as 'true' | 'false'] ?? '',
					fechaVencimiento: fechaVencimiento
						? parse(fechaVencimiento, FORMAT_DATE.FRONT, new Date())
						: undefined,
					fechaCreacion: fechaCreacion ? parse(fechaCreacion, FORMAT_DATE.WITH_HOUR, new Date()) : undefined,
				};
				reset(formData);
			} else {
				throw new Error('No data');
			}
		} catch (error) {
			openNotification(notificationTemplate.errorMessage('Error al obtener la solicitud'));
			navigate(ROUTES_TRAMITE.BORRADORES);
		} finally {
			setIsLoading(false);
			refLoading.current?.close();
		}
	}, []);

	const loadSetted = () => {
		if (!id || !user) return;

		loadData({
			nifEmisor: user?.nifEmisor ?? '',
			idTipoSector: user?.tipoSectorEmisor.id,
			idTramite: id,
		});
	};

	/** 
	 * Permitir cargar los datos desde diferentes páginas (Detalle documento, detalle sujeto, detalle general) 
	 * y solamente lo haga una única vez. En el caso de que se trate del mismo idTramite
	 */
	const loadSettedOneTime = () => {
		if (hasData && watch('id') === id) return;
		loadSetted();
	};

	useEffect(() => {
		if (autoLoad) {
			loadSettedOneTime();
		}
	}, [id]);

	return {
		isLoading,
		hasData,
		reload: loadSetted,
	};
};

export default useLoadTramite;
